import { Sorting } from "../types";

export enum TimeFrame {
  YearToDate = 0,
  Last2Years = 1,
  Last3Years = 2,
}

export enum MetricsUnit {
  Electricity = "kWh",
  Gas = "MJ",
  Water = "kL",
}

// Keep values equivalent to API keys
export enum MetricsType {
  Electricity = "Electricity",
  Gas = "Gas",
  Water = "Water",
}

// Keep values equivalent to API keys except ALL
export enum ReadingType {
  All = "ALL", // not an API key, includes all of reading types
  Actual = "ACTUAL",
  Estimated = "ESTIMATED",
  Interpolated = "INTERPOLATED",
  Forecast = "FORECAST",
}

export enum SortBy {
  TotalConsumption = "TotalConsumption",
  LatestConsumption = "LatestConsumption", // Only suitable for years or months, not siteIds,
  Key = "Key",
}

export type GroupBy =
  | "SiteInYears"
  | "SiteInMonths"
  | "YearInSites"
  | "YearInMonths"
  | "MonthInSites"
  | "MonthInYears"
  | "AllMonthInSites";

export type UtilityGroup = IndexByString<IndexByString<number>>;
export type UtilityConsumption = {
  [key in GroupBy]: UtilityGroup;
};

interface UtilityDatum {
  date: string;
  type: string; // ReadingType
  consumption: number;
  unit: string;
}

export interface Metrics {
  [key: string]: {
    // key is a metrics type
    [key: string]: {
      // key is a site identifier
      [key: string]: UtilityDatum[]; // key is a date format `yyyy-mm`
    };
  };
}

interface LoadingState {
  metricsLoading: boolean;
  metricsLoadingError: boolean;
  metricsLoaded: {
    [key in MetricsType]: boolean;
  };
}

interface TimeFrameState {
  timeFrameOption: TimeFrame;
  startDate: string;
  endDate: string;
}

interface SortingState {
  sortingDirection: {
    rankedYears: Sorting;
    rankedSites: Sorting;
  };
}

interface SelectedSites {
  selectedSites: string[];
}

interface MetricsState {
  metricsType: MetricsType;
  metricsUnit: MetricsUnit;
  metrics: Metrics;
}

export type State = LoadingState &
  TimeFrameState &
  SortingState &
  MetricsState &
  SelectedSites;

type LoadMetrics = { type: "LOAD_METRICS" };
type MetricsRequestSuccess = { type: "METRICS_REQUEST_SUCCESS" } & MetricsState;
type MetricsRequestFail = { type: "METRICS_REQUEST_FAIL" };
type SetTimeFrame = { type: "SET_TIME_FRAME" } & TimeFrameState;
type SetMetricsType = { type: "SET_METRICS_TYPE" } & {
  metricsType: MetricsType;
};
type SetSelectedSites = { type: "SET_SELECTED_SITES" } & SelectedSites;
type SetRankedSitesSortingDirection = {
  type: "SET_RANKED_SITES_SORTING_DIRECTION";
} & { rankedSites: Sorting };
type SetRankedYearsSortingDirection = {
  type: "SET_RANKED_YEARS_SORTING_DIRECTION";
} & { rankedYears: Sorting };

export type Action =
  | LoadMetrics
  | MetricsRequestSuccess
  | MetricsRequestFail
  | SetTimeFrame
  | SetMetricsType
  | SetSelectedSites
  | SetRankedSitesSortingDirection
  | SetRankedYearsSortingDirection;
